import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FaMoneyBillWave, FaPlus, FaEdit, FaTrash, FaPrint, FaCalendarAlt,
  FaSave, FaTimes, FaFileInvoiceDollar, FaTag, FaWallet, FaCreditCard
} from 'react-icons/fa';
import { NumericFormat } from 'react-number-format';
import Header from '../others/Header';
import config from '../config/config';
import { useToast } from '../components/ToastContext';
import ConfirmModal from '../components/ConfirmModal';

const { API_BASE_URL } = config;

// ── NOVO: categorias e formas de pagamento padrão — ajustar/expandir quando o back existir
const CATEGORIAS = [
  'Aluguel', 'Água/Luz/Internet', 'Material de Escritório', 'Combustível',
  'Manutenção', 'Impostos e Taxas', 'Salários', 'Marketing', 'Outros'
];

const FORMAS_PAGAMENTO = [
  { id: 'DINHEIRO', nome: 'Dinheiro' },
  { id: 'PIX', nome: 'PIX' },
  { id: 'DEBITO', nome: 'Cartão Débito' },
  { id: 'CREDITO', nome: 'Cartão Crédito' },
  { id: 'BOLETO', nome: 'Boleto' },
  { id: 'TRANSFERENCIA', nome: 'Transferência' }
];

function numeroParaReais(valor) {
  if (isNaN(valor) || valor === null) return 'R$ 0,00';
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function mesAtualFormatado() {
  const hoje = new Date();
  const mes = String(hoje.getMonth() + 1).padStart(2, '0');
  return `${hoje.getFullYear()}-${mes}`;
}

function nomeMesAno(mesFiltro) {
  const [ano, mes] = mesFiltro.split('-').map(Number);
  const data = new Date(ano, mes - 1, 1);
  const nome = data.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  return nome.charAt(0).toUpperCase() + nome.slice(1);
}

// ── NOVO: data e hora do registro (campo dataCadastro, gerado automaticamente pelo back)
function formatarDataHoraRegistro(dataCadastro) {
  if (!dataCadastro) return '-';
  const data = new Date(dataCadastro);
  if (isNaN(data.getTime())) return '-';
  return `${data.toLocaleDateString('pt-BR')} às ${data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

const Despesas = () => {
  const [mesFiltro, setMesFiltro] = useState(mesAtualFormatado());
  const [despesas, setDespesas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [gerandoRelatorio, setGerandoRelatorio] = useState(false);

  const [modalDespesa, setModalDespesa] = useState({ open: false, despesa: null });
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false, message: '', onConfirm: () => {}, title: 'Confirmação'
  });

  const { addToast } = useToast();

  const showConfirm = (message, onConfirm, title = 'Confirmação') =>
    setConfirmModal({ isOpen: true, message, onConfirm, title });
  const closeConfirm = () =>
    setConfirmModal({ isOpen: false, message: '', onConfirm: () => {}, title: 'Confirmação' });

  const getAuthHeader = () => {
    const token = localStorage.getItem('jwtToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  // ══════════════════════════════════════════════════════════════
  // CARGA DE DADOS
  // ══════════════════════════════════════════════════════════════
  // ── CONTRATO ESPERADO DO BACK (a implementar):
  // GET  /despesas/mes/{ano}/{mes}                -> lista de despesas do mês
  // POST /despesas                                -> cria despesa
  // PUT  /despesas/{idDespesa}                    -> atualiza despesa
  // DELETE /despesas/{idDespesa}                  -> exclui despesa
  // GET  /despesas/relatorio/{ano}/{mes}/pdf       -> PDF do relatório do mês (responseType blob)
  const carregarDespesas = async () => {
    const [ano, mes] = mesFiltro.split('-').map(Number);
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/despesas/mes/${ano}/${mes}`, getAuthHeader());
      setDespesas(response.data || []);
    } catch (err) {
      console.error('Erro ao carregar despesas:', err);
      setError(err.response?.data?.message || 'Erro ao carregar despesas');
      setDespesas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDespesas();
  }, [mesFiltro]);

  const totalMes = despesas.reduce((soma, d) => soma + (d.valor || 0), 0);

  // ══════════════════════════════════════════════════════════════
  // CRUD DESPESA
  // ══════════════════════════════════════════════════════════════
  const handleDespesaSalva = (despesaSalva) => {
    setDespesas(prev => {
      const existe = prev.some(d => d.idDespesa === despesaSalva.idDespesa);
      return existe
        ? prev.map(d => (d.idDespesa === despesaSalva.idDespesa ? despesaSalva : d))
        : [...prev, despesaSalva];
    });
    setModalDespesa({ open: false, despesa: null });
  };

  const handleDespesaExcluida = (idDespesa) => {
    setDespesas(prev => prev.filter(d => d.idDespesa !== idDespesa));
    setModalDespesa({ open: false, despesa: null });
  };

  // ══════════════════════════════════════════════════════════════
  // RELATÓRIO PDF DO MÊS
  // ══════════════════════════════════════════════════════════════
  const imprimirRelatorioMes = async () => {
    const [ano, mes] = mesFiltro.split('-').map(Number);
    setGerandoRelatorio(true);
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await axios.get(
        `${API_BASE_URL}/despesas/relatorio/${ano}/${mes}/pdf`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const novaJanela = window.open(url, '_blank');
      if (!novaJanela) {
        const link = document.createElement('a');
        link.href = url;
        link.download = `despesas-${mesFiltro}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      addToast('success', 'Relatório gerado com sucesso!');
    } catch (err) {
      console.error('Erro ao gerar relatório:', err);
      addToast('error', 'Erro ao gerar relatório: ' + (err.response?.data?.message || err.message));
    } finally {
      setGerandoRelatorio(false);
    }
  };

  return (
    <div style={styles.container}>
      <Header />
      <div style={styles.content}>
        <div style={styles.header}>
          <FaFileInvoiceDollar style={styles.titleIcon} />
          <h1 style={styles.title}>Despesas</h1>
        </div>

        {error && <div style={styles.errorMessage}>{error}</div>}

        {/* ══════════ FILTROS + AÇÕES ══════════ */}
        <div style={styles.painel}>
          <div style={styles.filtroLinha}>
            <div style={styles.filtroCampo}>
              <label style={styles.label}><FaCalendarAlt style={{ marginRight: 6 }} /> Mês</label>
              <input
                type="month"
                value={mesFiltro}
                onChange={(e) => setMesFiltro(e.target.value)}
                style={styles.inputMes}
              />
            </div>
            <div style={styles.filtroAcoes}>
              <button onClick={() => setModalDespesa({ open: true, despesa: null })} style={styles.primaryButton}>
                <FaPlus /> Nova Despesa
              </button>
              <button
                onClick={imprimirRelatorioMes}
                style={styles.secondaryButton}
                disabled={gerandoRelatorio || despesas.length === 0}
              >
                <FaPrint /> {gerandoRelatorio ? 'Gerando...' : 'Imprimir Relatório do Mês'}
              </button>
            </div>
          </div>

          <div style={styles.resumoBox}>
            <span style={styles.resumoLabel}>{nomeMesAno(mesFiltro)}</span>
            <span style={styles.resumoTotal}>
              <FaMoneyBillWave style={{ marginRight: 8 }} />
              Total: {numeroParaReais(totalMes)}
            </span>
          </div>
        </div>

        {/* ══════════ LISTA DE DESPESAS ══════════ */}
        <div style={styles.painel}>
          {loading ? (
            <p style={styles.loading}>Carregando...</p>
          ) : despesas.length > 0 ? (
            <div style={styles.tabelaWrapper}>
              <table style={styles.tabela}>
                <thead>
                  <tr>
                    <th style={styles.th}>Data</th>
                    <th style={styles.th}>Descrição</th>
                    <th style={styles.th}>Categoria</th>
                    <th style={styles.th}>Pagamento</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Valor</th>
                    <th style={styles.th}>Registrado em</th>
                    <th style={styles.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {despesas
                    .slice()
                    .sort((a, b) => new Date(a.dataDespesa) - new Date(b.dataDespesa))
                    .map(d => (
                      <tr key={d.idDespesa} style={styles.tr}>
                        <td style={styles.td}>{new Date(d.dataDespesa + 'T00:00:00').toLocaleDateString('pt-BR')}</td>
                        <td style={styles.td}>{d.descricao}</td>
                        <td style={styles.td}>
                          <span style={styles.categoriaTag}>{d.categoria}</span>
                        </td>
                        <td style={styles.td}>
                          {FORMAS_PAGAMENTO.find(f => f.id === d.formaPagamento)?.nome || d.formaPagamento || '-'}
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right', color: '#c0392b', fontWeight: '600' }}>
                          {numeroParaReais(d.valor)}
                        </td>
                        <td style={{ ...styles.td, ...styles.tdRegistradoEm }}>
                          {formatarDataHoraRegistro(d.dataCadastro)}
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>
                          <button
                            title="Editar"
                            onClick={() => setModalDespesa({ open: true, despesa: d })}
                            style={styles.iconButton}
                          >
                            <FaEdit />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={styles.noResults}>Nenhuma despesa cadastrada neste mês</p>
          )}
        </div>
      </div>

      {modalDespesa.open && (
        <ModalDespesa
          despesa={modalDespesa.despesa}
          onClose={() => setModalDespesa({ open: false, despesa: null })}
          onSaved={handleDespesaSalva}
          onDeleted={handleDespesaExcluida}
        />
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Excluir"
        cancelText="Cancelar"
      />
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// MODAL: CADASTRAR/EDITAR DESPESA
// ══════════════════════════════════════════════════════════════
const ModalDespesa = ({ despesa, onClose, onSaved, onDeleted }) => {
  const [descricao, setDescricao] = useState(despesa?.descricao || '');
  const [categoria, setCategoria] = useState(despesa?.categoria || CATEGORIAS[0]);
  const [valor, setValor] = useState(despesa?.valor ?? null);
  const [dataDespesa, setDataDespesa] = useState(despesa?.dataDespesa || new Date().toISOString().split('T')[0]);
  const [formaPagamento, setFormaPagamento] = useState(despesa?.formaPagamento || FORMAS_PAGAMENTO[0].id);
  const [observacoes, setObservacoes] = useState(despesa?.observacoes || '');
  const [errors, setErrors] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [confirmExcluir, setConfirmExcluir] = useState(false);

  const { addToast } = useToast();

  const validar = () => {
    const errs = {};
    if (!descricao) errs.descricao = 'Este campo é obrigatório';
    if (!valor || valor <= 0) errs.valor = 'Valor deve ser maior que zero';
    if (!dataDespesa) errs.dataDespesa = 'Este campo é obrigatório';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSalvar = async () => {
    if (!validar()) {
      addToast('warning', 'Preencha todos os campos obrigatórios corretamente!');
      return;
    }
    try {
      setSalvando(true);
      const token = localStorage.getItem('jwtToken');
      const dadosParaEnviar = { descricao, categoria, valor, dataDespesa, formaPagamento, observacoes };

      let response;
      if (despesa?.idDespesa) {
        response = await axios.put(
          `${API_BASE_URL}/despesas/${despesa.idDespesa}`,
          dadosParaEnviar,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/despesas`,
          dadosParaEnviar,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
      }

      addToast('success', despesa?.idDespesa ? 'Despesa atualizada com sucesso!' : 'Despesa cadastrada com sucesso!');
      onSaved(response.data);
    } catch (err) {
      console.error('Erro ao salvar despesa:', err);
      addToast('error', err.response?.data?.message || 'Erro ao salvar despesa');
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluir = async () => {
    try {
      setSalvando(true);
      const token = localStorage.getItem('jwtToken');
      await axios.delete(`${API_BASE_URL}/despesas/${despesa.idDespesa}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addToast('success', 'Despesa excluída com sucesso!');
      onDeleted(despesa.idDespesa);
    } catch (err) {
      console.error('Erro ao excluir despesa:', err);
      addToast('error', err.response?.data?.message || 'Erro ao excluir despesa');
    } finally {
      setSalvando(false);
      setConfirmExcluir(false);
    }
  };

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{despesa ? 'Editar Despesa' : 'Nova Despesa'}</h2>
          <button onClick={onClose} style={styles.modalClose} disabled={salvando}>&times;</button>
        </div>
        {despesa?.dataCadastro && (
          <div style={styles.registradoEmInfo}>
            Registrado em {formatarDataHoraRegistro(despesa.dataCadastro)}
          </div>
        )}
        <div style={styles.modalContent}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Descrição*</label>
            <input
              type="text"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Ex: Conta de energia elétrica"
              style={{ ...styles.input, ...(errors.descricao && styles.inputError) }}
              disabled={salvando}
            />
            {errors.descricao && <span style={styles.errorText}>{errors.descricao}</span>}
          </div>

          <div style={styles.formRow}>
            <div style={styles.inputGroup}>
              <label style={styles.label}><FaTag style={{ marginRight: 6 }} />Categoria</label>
              <select
                value={categoria}
                onChange={(e) => setCategoria(e.target.value)}
                style={styles.input}
                disabled={salvando}
              >
                {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Valor*</label>
              <NumericFormat
                value={valor}
                onValueChange={(values) => setValor(values.floatValue ?? null)}
                thousandSeparator="."
                decimalSeparator=","
                placeholder="R$ 0,00"
                decimalScale={2}
                fixedDecimalScale
                style={{ ...styles.input, ...(errors.valor && styles.inputError) }}
                disabled={salvando}
              />
              {errors.valor && <span style={styles.errorText}>{errors.valor}</span>}
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.inputGroup}>
              <label style={styles.label}><FaCalendarAlt style={{ marginRight: 6 }} />Data*</label>
              <input
                type="date"
                value={dataDespesa}
                onChange={(e) => setDataDespesa(e.target.value)}
                style={{ ...styles.input, ...(errors.dataDespesa && styles.inputError) }}
                disabled={salvando}
              />
              {errors.dataDespesa && <span style={styles.errorText}>{errors.dataDespesa}</span>}
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}><FaCreditCard style={{ marginRight: 6 }} />Forma de Pagamento</label>
              <select
                value={formaPagamento}
                onChange={(e) => setFormaPagamento(e.target.value)}
                style={styles.input}
                disabled={salvando}
              >
                {FORMAS_PAGAMENTO.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
              </select>
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Observações</label>
            <textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              placeholder="Informações adicionais (opcional)"
              rows="3"
              style={styles.textarea}
              disabled={salvando}
            />
          </div>
        </div>
        <div style={styles.modalFooter}>
          {despesa?.idDespesa && (
            <button onClick={() => setConfirmExcluir(true)} style={styles.deleteButtonModal} disabled={salvando}>
              <FaTrash /> Excluir
            </button>
          )}
          <button onClick={onClose} style={styles.secondaryButton} disabled={salvando}>
            <FaTimes /> Cancelar
          </button>
          <button onClick={handleSalvar} style={styles.primaryButton} disabled={salvando}>
            {salvando ? 'Salvando...' : (<><FaSave /> {despesa ? 'Atualizar' : 'Salvar'}</>)}
          </button>
        </div>

        <ConfirmModal
          isOpen={confirmExcluir}
          onClose={() => setConfirmExcluir(false)}
          onConfirm={handleExcluir}
          title="Excluir despesa"
          message="Tem certeza que deseja excluir esta despesa?"
          confirmText="Excluir"
          cancelText="Cancelar"
        />
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// ESTILOS — paleta Nexus Despachante (#004d4d / #008080 / #20b2aa)
// ══════════════════════════════════════════════════════════════
const TEAL_DARK = '#004d4d';
const TEAL = '#008080';
const TEAL_LIGHT_ACCENT = '#20b2aa';
const RED = '#c0392b';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    color: '#333',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
  },
  content: {
    padding: '24px',
    maxWidth: '1100px',
    margin: '0 auto',
    width: '100%'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px'
  },
  titleIcon: {
    fontSize: '26px',
    color: TEAL
  },
  title: {
    fontSize: '22px',
    margin: 0,
    color: TEAL_DARK,
    fontWeight: '700'
  },
  errorMessage: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '12px',
    borderRadius: '4px',
    marginBottom: '20px'
  },

  painel: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '10px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
    marginBottom: '20px'
  },

  filtroLinha: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '16px'
  },
  filtroCampo: {
    display: 'flex',
    flexDirection: 'column'
  },
  filtroAcoes: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap'
  },
  inputMes: {
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px'
  },

  resumoBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#f0fdfa',
    border: `1px solid ${TEAL_LIGHT_ACCENT}55`,
    borderRadius: '8px',
    padding: '12px 16px'
  },
  resumoLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: TEAL_DARK
  },
  resumoTotal: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '16px',
    fontWeight: '700',
    color: RED
  },

  tabelaWrapper: {
    overflowX: 'auto'
  },
  tabela: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    borderBottom: `2px solid ${TEAL}22`,
    color: TEAL_DARK,
    fontWeight: '700',
    fontSize: '13px'
  },
  tr: {
    borderBottom: '1px solid #f0f0f0'
  },
  td: {
    padding: '10px 12px',
    verticalAlign: 'middle'
  },
  tdRegistradoEm: {
    color: '#7f8c8d',
    fontSize: '12px',
    whiteSpace: 'nowrap'
  },
  categoriaTag: {
    backgroundColor: '#e0f2f1',
    color: TEAL_DARK,
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600'
  },
  iconButton: {
    width: '32px',
    height: '32px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: '#eaf6f6',
    color: TEAL,
    cursor: 'pointer'
  },

  loading: {
    textAlign: 'center',
    padding: '30px',
    color: '#7f8c8d'
  },
  noResults: {
    textAlign: 'center',
    padding: '30px',
    color: '#95a5a6',
    fontStyle: 'italic'
  },

  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    fontSize: '14px',
    color: '#34495e'
  },
  input: {
    width: '95%',
    padding: '11px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px'
  },
  inputError: {
    borderColor: '#ff4444'
  },
  errorText: {
    color: '#ff4444',
    fontSize: '12px',
    marginTop: '4px',
    display: 'block'
  },
  textarea: {
    width: '95%',
    padding: '11px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    resize: 'vertical'
  },
  formRow: {
    display: 'flex',
    gap: '20px',
    marginBottom: '14px',
    flexWrap: 'wrap'
  },
  inputGroup: {
    flex: '1',
    minWidth: '180px',
    marginBottom: '14px'
  },

  primaryButton: {
    padding: '10px 16px',
    backgroundColor: TEAL,
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600'
  },
  secondaryButton: {
    padding: '10px 16px',
    backgroundColor: '#95a5a6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600'
  },
  deleteButtonModal: {
    padding: '10px 16px',
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    marginRight: 'auto'
  },

  modalOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '10px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '18px 20px',
    borderBottom: '1px solid #eee'
  },
  modalTitle: {
    fontSize: '18px',
    margin: 0,
    color: TEAL_DARK
  },
  modalClose: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#9ca3af'
  },
  modalContent: {
    padding: '20px'
  },
  registradoEmInfo: {
    padding: '8px 20px',
    fontSize: '12px',
    color: '#7f8c8d',
    backgroundColor: '#fafafa',
    borderBottom: '1px solid #eee'
  },
  modalFooter: {
    padding: '18px 20px',
    borderTop: '1px solid #eee',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px'
  }
};

export default Despesas;
